import React, { useState } from 'react';
import { Card, CardHeader, CardContent, Input, Button, PaginatedTable, Field, FieldLabel } from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';
import { Loader2 } from 'lucide-react';

interface PayoutItem {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created?: number;
}

export default function PayoutsPage() {
  const [academyId, setAcademyId] = useState('');
  const [payouts, setPayouts] = useState<PayoutItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLoad = () => {
    if (!academyId.trim()) {
      setError('Enter an academy ID');
      return;
    }
    setLoading(true);
    setError(null);
    adminBackofficeApi
      .getPayouts({ academyId: academyId.trim(), limit: 50 })
      .then((data) => {
        if (data.success && data.data?.payouts) setPayouts(data.data.payouts);
        else setPayouts([]);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || err?.message || 'Failed to load');
        setPayouts([]);
      })
      .finally(() => setLoading(false));
  };

  return (
    <div>
      <PageHeader
        title="Payouts"
        help={<p>Look up Stripe Connect payouts by academy ID.</p>}
      />
      <Card className="mb-6">
        <CardHeader>
          <h2 className="text-lg font-medium">By academy</h2>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2 items-end">
          <Field className="flex-1 min-w-[200px]">
            <FieldLabel htmlFor="payouts-academy-id">Academy ID</FieldLabel>
            <Input id="payouts-academy-id" value={academyId} onChange={(e) => setAcademyId(e.target.value)} placeholder="Academy ID" />
          </Field>
          <Button onClick={handleLoad} disabled={loading || !academyId.trim()}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Load payouts'}
          </Button>
        </CardContent>
      </Card>
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300 mb-6">
          {error}
        </div>
      )}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-medium">Payout list</h2>
        </CardHeader>
        <CardContent>
          <PaginatedTable<PayoutItem>
            columns={[
              { key: 'id', header: 'ID', render: (p) => <span className="font-mono text-xs">{p.id}</span> },
              {
                key: 'amount',
                header: 'Amount',
                className: 'text-right',
                render: (p) => `${(p.amount / 100).toFixed(2)} ${p.currency}`,
              },
              { key: 'status', header: 'Status', render: (p) => p.status },
              {
                key: 'created',
                header: 'Created',
                render: (p) => (p.created ? new Date(p.created * 1000).toLocaleString() : '–'),
              },
            ]}
            data={payouts}
            pagination={null}
            loading={loading}
            emptyMessage="Enter an academy ID and click Load payouts."
            onPageChange={() => {}}
          />
        </CardContent>
      </Card>
    </div>
  );
}
